import { followSharedTooltipCursor } from "../../utils/chart-tooltip";
import {
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Input,
  Renderer2,
  signal,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import ApexCharts from "apexcharts";
import { AuthService } from "../../../services/api/auth/auth.service";
import { UserFieldService } from "../../../services/user-field/user-field.service";
import { ThemeService } from "../../../services/theme/theme.service";
import { finalize, firstValueFrom } from "rxjs";
import {
  perennialTreeMean,
  perennialTreeQ15,
  perennialTreeQ85,
} from "../../data/perennial-tree-data";
import moment, { Moment } from "moment-timezone";
import {
  currentDate,
  currentDatePast,
  endDateWaterYear,
  startDateWaterYear,
  startDateEtWaterYear,
  startDateCalendarWaterYear,
  startDateWaterYearPast,
  today,
} from "../../../field-claim/map/util/utliWaterYear";
import {
  convertAllETToCSV,
  convertInchesToDisplayUnit,
  cropDict,
  escapeCSV,
  ET_INCH_FIELDS,
  formatNumber,
  Role,
} from "../../utils/data-util";
import { GraphPopoverInstructionComponent } from "../../utils/graph-popover-instruction/graph-popover-instruction.component";
import { InfoPopoverComponent } from "../../utils/info-popover/info-popover.component";
import { LoadingService } from "../../../services/loading/loading";
import {
  AppliedWaterCalculationPayload,
  AppliedWaterCalculationResponse,
  DataService,
  IrrigatedInfo,
} from "../../../services/api/data/data.service";
import { CommonModule } from "@angular/common";
import { SafeHtml } from "@angular/platform-browser";
import { SnackbarService } from "../../../services/snackbar/snackbar.service";
type ApexSeriesWithoutData = Array<Omit<ApexAxisChartSeries[number], "data">>;

type AppliedWaterMonth = {
  key: string;
  label: string;
  selected: boolean;
  surfaceWater: number | null;
  groundWater: number | null;
};

type WaterDisplayUnit = "inches" | "acreFeet" | "feet";

@Component({
  selector: "app-dashboard-chart",
  standalone: true,
  imports: [
    GraphPopoverInstructionComponent,
    InfoPopoverComponent,
    CommonModule,
  ],
  templateUrl: "./dashboard-chart.html",
  styleUrl: "./dashboard-chart.scss",
})
export class DashboardChart {
  @Input() type!: Role;
  @Input() claimedFieldsForChart?: ClaimedField[];
  @ViewChild("chart") chartElement!: ElementRef;
  @ViewChild("chartContainer")
  chartContainerElement!: ElementRef<HTMLDivElement>;
  @ViewChild("dropdownButton") dropdownButton!: ElementRef;
  @ViewChild("dropdownMenu") dropdownMenu!: ElementRef;
  @ViewChild("chartContainer") chartContainer!: ElementRef;
  @ViewChild("appliedWaterChart")
  appliedWaterChartElement?: ElementRef<HTMLDivElement>;
  private currentMode: string = "";
  readonly isDemoMode: boolean;
  readonly isActiveAppliedWater: boolean;
  get canViewAppliedWaterEstimate(): boolean {
    if (this.isDemoMode) return false;
    if (this.type === "manager") return this.isActiveAppliedWater;
    return this.selectedclaimedField?.is_active_applied_water === true;
  }
  loadingService = inject(LoadingService);
  isDownloadAllDisabled = signal(false);
  private readonly destroyRef = inject(DestroyRef);
  isOpen = false;
  claimedFields?: ClaimedField[];
  claimedFieldETs?: any;
  fieldWeather?: any;
  selectedclaimedField?: ClaimedField;
  title?: string;
  isPerennailTree?: boolean;
  isPerennialTreeMode = false;
  private previousPerennailTreeMode = false;
  private chartContainerHeightLocked = false;
  clickListener?: () => void;
  private fullscreenListener?: () => void;
  isFullscreen = false;
  isWeatherModeInfoVisible = false;
  isAppliedWaterEstimateInfoVisible = false;
  chart?: any;
  // Keep independent legend visibility maps to avoid state bleed between chart modes
  // (ET and Weather both contain "Daily Precipitation" but should not share visibility).
  private etSeriesHiddenState = new Map<string, boolean>();
  private weatherSeriesHiddenState = new Map<string, boolean>();
  private perennialSeriesHiddenState = new Map<string, boolean>();
  private readonly defaultHiddenEtSeries = new Set(["Cumulative ET"]);
  private weatherDataCache = new Map<string, any>();
  private etDataCache = new Map<number, any[]>();
  mode = localStorage.getItem("theme");
  timeRange: string = "All Water Year";
  timeRangeFilter: { [key: string]: number } = {
    "Last 7 days": 7,
    "Last 15 days": 15,
    "Last 30 days": 30,
    "Last 60 days": 60,
    "All Water Year": 367,
  };
  // Define cropDict
  private cropDict: { [key: string]: string };
  private readonly CSVHeaders = [
    "field_name",
    "entity",
    "crop_type",
    "et_date",
    "acres",
    "eta",
    "ceta",
    "prcp",
    "cprcp",
  ];
  private readonly CSVHeadersManagerOrEntity = [
    "et_date",
    "acres",
    "eta",
    "ceta",
    "prcp",
    "cprcp",
  ];
  private buildEtCsvHeaderAliasesForUserType(
    audience: "grower" | "managerOrEntity",
  ): string[] {
    const unitLabel = this.displayUnitLabel(true);
    const growerOnlyColumns =
      audience === "grower" ? ["Field Name", "Entity Name", "Crop Type"] : [];
    return [
      ...growerOnlyColumns,
      "Date",
      "Acres",
      `Evapotranspiration (${unitLabel})`,
      `Cumulative Evapotranspiration (${unitLabel})`,
      `Precipitation (${unitLabel})`,
      `Cumulative Precipitation (${unitLabel})`,
    ];
  }
  private convertEtRowsToDisplayUnit(rows: any[]): any[] {
    if (this.isInchesUnit()) return rows;
    return rows.map((row) => {
      const next: any = { ...row };
      ET_INCH_FIELDS.forEach((field: string) => {
        const raw = row[field];
        next[field] = typeof raw === "number" ? this.toDisplayUnit(raw) : raw;
      });
      return next;
    });
  }
  currentDate: Moment;
  private etDisplayStart: Moment;
  todayDate: any;
  // Define the weatherMode
  isWeatherMode: boolean = false;
  isAppliedWaterEstimateMode: boolean = false;
  appliedWaterDirty = false;
  isAppliedWaterLoading = false;
  isAppliedWaterSaving = false;
  private appliedWaterRequestSeq = 0;
  private appliedWaterSourcesReadyFieldId?: number;
  private lastAppliedWaterPreviewKey = "";
  private appliedWaterPreviewRequestSeq = 0;
  isAppliedWaterPreviewLoading = false;
  appliedWaterPreviewResult: AppliedWaterCalculationResponse | null = null;
  private appliedWaterChart?: ApexCharts;
  // Defaults are seeded once per page; rebuilds retain the user's selections.
  private readonly appliedWaterSeriesHiddenState = new Map<string, boolean>([
    ["Monthly Groundwater Applied - High", true],
    ["Monthly Groundwater Applied - Low", true],
    ["Target Water", true],
  ]);
  private appliedWaterRenderTimer?: ReturnType<typeof setTimeout>;
  appliedWaterMonths: AppliedWaterMonth[] = this.buildAppliedWaterMonths();
  displayUnit = signal<WaterDisplayUnit>("inches");
  isInchesUnit = (): boolean => this.displayUnit() === "inches";
  isAcreFeetUnit = (): boolean => this.displayUnit() === "acreFeet";
  isFeetUnit = (): boolean => this.displayUnit() === "feet";
  setDisplayUnit(unit: WaterDisplayUnit): void {
    if (unit === "acreFeet" && !this.selectedclaimedField?.acres) return;
    this.displayUnit.set(unit);
  }
  displayUnitLabel(short = false): string {
    if (this.isAcreFeetUnit()) return short ? "ac-ft" : "Acre-Feet";
    if (this.isFeetUnit()) return short ? "ft" : "Feet";
    return short ? "in" : "Inches";
  }
  private toDisplayUnit = (
    inches: number | null | undefined,
  ): number | null => {
    if (inches == null) return null;
    if (this.isFeetUnit()) return inches / 12;
    return convertInchesToDisplayUnit(
      inches,
      this.selectedclaimedField?.acres,
      this.isAcreFeetUnit(),
    );
  };
  // Used to show a chart-level loading spinner while waiting for weather API during field switches.
  isWeatherFieldSwitchLoading = false;
  private weatherFieldSwitchRequestSeq = 0;
  private fieldWeatherFieldId?: number;
  isEtFieldSwitchLoading = false;
  private etFieldSwitchRequestSeq = 0;
  // The initialized chartOptions
  public chartOptions: any = {
    series: [
      {
        name: "Daily Precipitation",
        data: [],
        color: "#06b6d4",
      },
      {
        name: "Daily ET",
        data: [],
        color: "#1A56DB",
      },
      {
        name: "Cumulative ET",
        data: [],
        color: "#7E3BF2",
        hidden: true,
      },
    ],
    chart: {
      events: { mouseMove: followSharedTooltipCursor },
      sparkline: {
        enabled: false,
      },
      height: "100%",
      grid: {
        padding: {
          bottom: 5, // Increase this to create more space at the bottom
          left: 50,
        },
      },
      type: "area",
      fontFamily: "Inter, sans-serif",
      dropShadow: {
        enabled: false,
      },
      toolbar: {
        show: true,
        offsetX: -20,
        tools: {
          reset: true,
          zoom: true,
          selection: false,
          download: false,
          zoomin: false,
          zoomout: false,
          pan: false,
        },
      },
      zoom: {
        autoScaleYaxis: false,
      },
    },
    xaxis: {
      crosshairs: { show: false },
      show: true,
      categories: [],
      labels: {
        show: true,
        style: {
          fontFamily: "Inter, sans-serif",
          cssClass: "text-xs font-normal fill-gray-500 dark:fill-gray-400",
        },
        tickAmount: 10,
        rotate: -30,
        rotateAlways: false,
        hideOverlappingLabels: true,
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    yaxis: [
      {
        seriesName: "Daily ET",
        title: {
          text: "Evapotranspiration (Inches)",
          style: {
            fontFamily: "Inter, sans-serif",
            cssClass: "text-xs font-normal fill-gray-500 dark:fill-gray-400",
          },
        },
        show: true,
        labels: {
          show: true,
          style: {
            fontFamily: "Inter, sans-serif",
            cssClass: "text-xs font-normal fill-gray-500 dark:fill-gray-400",
          },
          formatter: function (value: number) {
            return value;
          },
        },
      },
      {
        seriesName: "Daily Precipitation",
        opposite: true,
        title: {
          text: "Daily Precip (Inches)",
          style: {
            fontFamily: "Inter, sans-serif",
            cssClass: "text-xs font-normal fill-gray-500 dark:fill-gray-400",
          },
        },
        show: true,
        min: 0,
        labels: {
          show: true,
          style: {
            fontFamily: "Inter, sans-serif",
            cssClass: "text-xs font-normal fill-gray-500 dark:fill-gray-400",
          },
          formatter: function (value: number) {
            return value; //.toFixed(1);
          },
        },
      },
    ],
    fill: {
      type: "gradient",
      gradient: {
        opacityFrom: 0.55,
        opacityTo: 0.1,
      },
    },
    dataLabels: {
      enabled: false,
    },
    legend: {
      show: true,
      position: "top",
      labels: {
        colors: "#64748b",
      },
    },
    grid: {
      show: true,
    },
  };
  private legendListenerCleanup?: () => void;
  // Pending hide timer — set when the cursor leaves the legend or popover,
  // cleared if it re-enters either side. Gives the cursor time to bridge
  // the 8px gap between legend and popover so the link is reachable.
  private popoverHideTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private static POPOVER_HIDE_DELAY_MS = 150;

  // Drives the @if + position bindings in the template.
  weatherLegendPopover = signal<{
    show: boolean;
    content: SafeHtml;
    pos: { top: number; left: number };
  }>({
    show: false,
    content: "",
    pos: {
      top: 0,
      left: 0,
    },
  });

  private cancelPopoverHide() {
    if (this.popoverHideTimeoutId !== null) {
      clearTimeout(this.popoverHideTimeoutId);
      this.popoverHideTimeoutId = null;
    }
  }

  /**
   * So popover doesn't turn off right away
   */
  private schedulePopoverHide() {
    this.cancelPopoverHide();
    this.popoverHideTimeoutId = setTimeout(() => {
      this.weatherLegendPopover.set({
        show: false,
        content: "",
        pos: { top: 0, left: 0 },
      });
      this.popoverHideTimeoutId = null;
    }, DashboardChart.POPOVER_HIDE_DELAY_MS);
  }

  // Template handlers — wired to the popover <div>'s mouseenter/mouseleave.
  onPopoverEnter = () => this.cancelPopoverHide();
  onPopoverLeave = () => this.schedulePopoverHide();

  readonly weatherModeInfoText = `The historical temperature data displayed below is collected by the California Department of Water Resource as part of the California Irrigation Management Information System (CIMIS). The forecasted temperature data displayed below is produced by the National Weather Service. Values displayed below represent data recorded at the closest CIMIS station location to the respective field.`;

  readonly appliedWaterEstimateInfoText = `An applied water estimate is provided based on total ET and assumptions about total precipitation, irrigation efficiency based on the irrigation method, and reliance on surface water versus groundwater.`;

  readonly onAppliedWaterEstimateInfoEnter = () => {
    this.isAppliedWaterEstimateInfoVisible = true;
  };

  readonly onAppliedWaterEstimateInfoLeave = () => {
    this.isAppliedWaterEstimateInfoVisible = false;
  };

  readonly onWeatherModeInfoEnter = () => {
    this.isWeatherModeInfoVisible = true;
  };

  readonly onWeatherModeInfoLeave = () => {
    this.isWeatherModeInfoVisible = false;
  };

  private bindWeatherLegendPopover = (apexCtx: any, config: any) => {
    this.legendListenerCleanup?.(); // Clean up any existing listeners before adding new ones

    const apexCtxLegendWrap: HTMLElement | undefined =
      apexCtx.w.globals.dom.elLegendWrap;
    if (!apexCtxLegendWrap) {
      console.warn(
        "ApexCharts legend wrapper element not found for popover binding.",
      );
      return;
    }

    // Resolve the legend <div> for a given series name, or null if not found.
    const findLegendEl = (seriesName: string): HTMLElement | null => {
      const idx = apexCtx.w.globals.seriesNames.findIndex(
        (name: string) => name === seriesName,
      );
      if (idx === -1) {
        console.warn(
          `Series with name "${seriesName}" not found for popover binding.`,
        );
        return null;
      }
      const el = apexCtxLegendWrap.children[idx] as HTMLElement | undefined;
      if (!el) {
        console.warn(`Legend element for series "${seriesName}" not found.`);
        return null;
      }
      return el;
    };

    // Wire mouseenter/mouseleave on `el` to show `content` in the popover.
    // Returns a cleanup that removes both listeners.
    const bindPopoverFor = (el: HTMLElement, content: string): (() => void) => {
      const enter = this.renderer.listen(el, "mouseenter", () => {
        this.cancelPopoverHide();
        const rect = el.getBoundingClientRect();
        this.weatherLegendPopover.set({
          show: true,
          content,
          pos: { top: rect.top, left: rect.left },
        });
      });
      const leave = this.renderer.listen(el, "mouseleave", () => {
        this.schedulePopoverHide();
      });
      return () => {
        enter();
        leave();
      };
    };

    const cleanups: Array<() => void> = [];

    //! Growing Degree Days
    const growingDegreeDayLegendEl = findLegendEl("Growing Degree Days");
    if (growingDegreeDayLegendEl) {
      cleanups.push(
        bindPopoverFor(
          growingDegreeDayLegendEl,
          "The cumulative number of days where the average temp. was higher than 44.6 deg. F. Temperature data is sourced from the nearest CIMIS station.",
        ),
      );
    }

    //! Chilling Portions
    const chillingPortionsLegendEl = findLegendEl("Chilling Portions");
    if (chillingPortionsLegendEl) {
      cleanups.push(
        bindPopoverFor(
          chillingPortionsLegendEl,
          `This is a dynamic model developed by UC Davis taking into account the quality of chilling hours. Hours with temperatures ranging from 43 deg. F - 47 deg. F have the greatest chilling value. Temperatures outside that range falling to 32 deg. F and rising to 54 deg. F still have some chill value, but to a lesser degree. For more information see: <a href="https://fruitsandnuts.ucdavis.edu/chill-calculator" target="_blank" rel="noopener noreferrer" class="text-blue-600 dark:text-blue-400 underline">Chill Calculator</a>`,
        ),
      );
    }

    this.legendListenerCleanup = () => {
      cleanups.forEach((fn) => fn());
      this.cancelPopoverHide();
      this.weatherLegendPopover.set({
        show: false,
        content: "",
        pos: { top: 0, left: 0 },
      });
    };
  };

  // Constructor
  constructor(
    private renderer: Renderer2,
    private authService: AuthService,
    private userFieldService: UserFieldService,
    private themeService: ThemeService,
    private dataService: DataService,
    private snackbarService: SnackbarService,
  ) {
    this.destroyRef.onDestroy(() => {
      this.chart = null;
      this.clickListener?.();
      this.fullscreenListener?.();
      this.legendListenerCleanup?.();
      this.cancelPopoverHide();
      this.destroyAppliedWaterChart();
    });
    this.cropDict = cropDict;
    this.currentMode = this.authService.internalModeValue;
    this.isDemoMode = this.currentMode === "demo";
    this.isActiveAppliedWater = this.authService.getIsActiveAppliedWater();
    if (this.currentMode === "demo") {
      this.currentDate = currentDatePast();
      this.etDisplayStart = startDateWaterYearPast();
    } else {
      this.currentDate = currentDate();
      this.etDisplayStart = startDateEtWaterYear();
      this.todayDate = today();
    }
    // Seed ET legend defaults from ET chart options.
    this.seedSeriesHiddenState(
      this.chartOptions.series,
      this.etSeriesHiddenState,
    );
    // Seed Weather legend defaults explicitly. Weather series are built dynamically in updateCharts,
    // so we define their initial hidden/visible state once here.
    this.seedSeriesHiddenState(
      [
        { name: "Min Temperature" },
        { name: "Forecasted MinT" },
        { name: "Historical Avg MinT" },
        { name: "Max Temperature" },
        { name: "Forecasted MaxT" },
        { name: "Historical Avg MaxT" },
        { name: "Daily Precipitation" },
        { name: "Cumulative Precipitation", hidden: true },
        { name: "Growing Degree Days", hidden: true },
        { name: "Chilling Portions", hidden: true },
      ],
      this.weatherSeriesHiddenState,
    );
    // Seed Perennial legend defaults explicitly (perennial series are built dynamically in updateCharts).
    this.seedSeriesHiddenState(
      [
        { name: "current cumulative ET" },
        { name: "forecasted cumulative ET" },
        { name: "historical cumulative ET - 85th percentile" },
        { name: "historical cumulative average ET" },
        { name: "historical cumulative ET - 15th percentile" },
      ],
      this.perennialSeriesHiddenState,
    );
    // Attach legend click hooks so every user toggle is reflected in the corresponding map in real time.
    this.attachLegendStateTracking();

    effect(() => {
      // Update units when mode changes, but preserve legend state and other chart options.
      this.isInchesUnit();
      if (this.isAppliedWaterEstimateMode) {
        this.renderAppliedWaterChart();
        return;
      }
      if (!this.selectedclaimedField || !Array.isArray(this.claimedFieldETs)) {
        return;
      }
      this.updateCharts(false);
    });
  }

  private seedSeriesHiddenState(
    series: ApexSeriesWithoutData,
    hiddenStateMap: Map<string, boolean>,
  ): void {
    series.forEach((seriesItem) => {
      if (
        typeof seriesItem?.name === "string" &&
        seriesItem.name.trim().length > 0
      ) {
        // Support both Apex `hidden` and legacy `show: false` patterns.
        const legacyShow = (seriesItem as { show?: boolean }).show;
        const isHidden =
          typeof seriesItem.hidden === "boolean" ? seriesItem.hidden
          : typeof legacyShow === "boolean" ? !legacyShow
          : false;
        hiddenStateMap.set(seriesItem.name, isHidden);
      }
    });
  }

  private attachLegendStateTracking(): void {
    const createLegendHandler =
      (resolveHiddenStateMap: () => Map<string, boolean>) =>
      (chartContext: any): void => {
        // Apex updates collapse/visibility after the legend click event fires.
        // Capture in the next macrotask so we read the final, post-toggle state.
        setTimeout(
          () =>
            this.captureSeriesHiddenState(
              chartContext,
              resolveHiddenStateMap(),
            ),
          0,
        );
      };

    const priorEvents = this.chartOptions.chart?.events ?? {};
    const priorMounted = priorEvents.mounted;
    this.chartOptions.chart = {
      ...this.chartOptions.chart,
      events: {
        ...priorEvents,
        // Main chart is shared by ET, Weather, and Perennial. Route legend state to the active mode map.
        legendClick: createLegendHandler(() =>
          this.isPerennialTreeMode ? this.perennialSeriesHiddenState
          : this.isWeatherMode ? this.weatherSeriesHiddenState
          : this.etSeriesHiddenState,
        ),
        mounted: (ctx: any, config: any) => {
          this.lockChartContainerHeight();
          priorMounted?.(ctx, config);
        },
      },
    };
  }

  private captureSeriesHiddenState(
    chartInstance: any,
    hiddenStateMap: Map<string, boolean>,
  ): void {
    // Read current legend state directly from Apex internals:
    // - seriesNames gives stable series identity
    // - collapsedSeriesIndices indicates which legend items are hidden
    const seriesNames: string[] | undefined =
      chartInstance?.w?.globals?.seriesNames;
    if (!Array.isArray(seriesNames) || seriesNames.length === 0) return;

    const collapsedSeriesIndices = new Set<number>(
      chartInstance?.w?.globals?.collapsedSeriesIndices ?? [],
    );
    seriesNames.forEach((name, index) => {
      if (typeof name === "string" && name.trim().length > 0) {
        hiddenStateMap.set(name, collapsedSeriesIndices.has(index));
      }
    });
  }

  private applyPersistedSeriesHiddenState(
    series: ApexAxisChartSeries,
    hiddenStateMap: Map<string, boolean>,
    forceHiddenSeries = new Set<string>(),
  ) {
    // Build series options by applying persisted visibility when available.
    // If a series has never been toggled, keep the default defined in the incoming series object.
    return series.map((seriesItem) => {
      if (typeof seriesItem.name !== "string") return seriesItem;
      if (forceHiddenSeries.has(seriesItem.name)) {
        return { ...seriesItem, hidden: true };
      }
      const persistedHidden = hiddenStateMap.get(seriesItem.name);
      return typeof persistedHidden === "boolean" ?
          { ...seriesItem, hidden: persistedHidden }
        : seriesItem;
    });
  }

  private syncRenderedSeriesHiddenState(
    series: ApexAxisChartSeries,
    hiddenStateMap: Map<string, boolean>,
  ): void {
    // Apex can preserve collapsed legend indices across series swaps. Re-apply by
    // name after rendering so defaults do not bleed between ET and Weather modes.
    setTimeout(() => {
      series.forEach((seriesItem) => {
        if (typeof seriesItem.name !== "string") return;
        const isHidden =
          hiddenStateMap.get(seriesItem.name) ?? !!seriesItem.hidden;
        if (isHidden) {
          this.chart?.hideSeries(seriesItem.name);
        } else {
          this.chart?.showSeries(seriesItem.name);
        }
      });
    }, 0);
  }

  // Function triggers when toggling the forcast mode
  onToggleForcastingMode(event: Event): void {
    // Snapshot current legend state before switching modes
    const currentStateMap =
      this.isPerennialTreeMode ?
        this.perennialSeriesHiddenState
      : this.etSeriesHiddenState;
    this.captureSeriesHiddenState(this.chart, currentStateMap);

    this.isPerennialTreeMode = (event.target as HTMLInputElement).checked;

    // Reinitialize the chart so legends and axes are fully swapped
    this.reinitializeMainChart();
    this.updateCharts(true);
  }

  // Function triggers when time fielter changes
  onTimeFilterChange(range: string) {
    this.timeRange = range;
    this.closeDropdown();
    this.updateCharts(false);
  }

  onChartTypeModeChange(mode: "et" | "appliedWater" | "weather"): void {
    if (mode === "appliedWater" && !this.canViewAppliedWaterEstimate) return;
    if (mode === "appliedWater") this.destroyAppliedWaterChart();
    // Snapshot current chart legend state *before* mode flip/reinitialize.
    // This prevents ET and Weather from overwriting each other's map.
    const currentMainChartStateMap =
      this.isWeatherMode ?
        this.weatherSeriesHiddenState
      : this.etSeriesHiddenState;
    this.captureSeriesHiddenState(this.chart, currentMainChartStateMap);
    if (this.isDemoMode && mode === "weather") mode = "et";
    const isWeatherMode = mode === "weather";
    const isAppliedWaterEstimateMode = mode === "appliedWater";
    if (isWeatherMode || isAppliedWaterEstimateMode) {
      this.previousPerennailTreeMode = this.isPerennialTreeMode;
      this.isPerennialTreeMode = false;
    } else {
      this.isPerennialTreeMode = this.previousPerennailTreeMode;
    }
    this.reinitializeMainChart();
    this.isWeatherMode = isWeatherMode;
    this.isAppliedWaterEstimateMode = isAppliedWaterEstimateMode;
    if (!this.isAppliedWaterEstimateMode) {
      this.destroyAppliedWaterChart();
    }
    if (this.isAppliedWaterEstimateMode) {
      this.loadAppliedWaterInfo();
    }
    if (this.isWeatherMode) {
      this.loadEtForSelectedField(true, true);
    } else {
      this.loadEtForSelectedField(true);
    }
  }

  private isNonIrrigatedMonth(
    field: ClaimedField | undefined,
    month: Moment,
  ): boolean {
    if (!field) return false;
    const fieldWithMethods = field as ClaimedField &
      Record<string, string[] | null | undefined>;
    const isNonIrrigated = (cropNumber: string) =>
      fieldWithMethods[`irrigation_methods_${cropNumber}`]?.[0]
        ?.trim()
        .toLowerCase() === "non-irrigated";

    // Perennial fields use one method for the entire water year.
    if (field.isperennialtree === true) return isNonIrrigated("01");

    const monthStart = month.clone().startOf("month");
    const monthEnd = month.clone().endOf("month");
    return [
      { number: "01", start: field.ls_start_01, end: field.ls_end_01 },
      { number: "02", start: field.ls_start_02, end: field.ls_end_02 },
      { number: "03", start: field.ls_start_03, end: field.ls_end_03 },
    ].some(
      (crop) =>
        isNonIrrigated(crop.number) &&
        !!crop.start &&
        !!crop.end &&
        moment(crop.start).startOf("day").isSameOrBefore(monthEnd) &&
        moment(crop.end).endOf("day").isSameOrAfter(monthStart),
    );
  }
  private buildAppliedWaterMonths(field?: ClaimedField): AppliedWaterMonth[] {
    const start = startDateWaterYear();
    const end = endDateWaterYear();
    const months: AppliedWaterMonth[] = [];
    const cursor = start.clone().startOf("month");
    const annualCropWindows =
      field ?
        (
          [
            [field.ls_start_01, field.ls_end_01],
            [field.ls_start_02, field.ls_end_02],
            [field.ls_start_03, field.ls_end_03],
          ] as Array<[string | Date | null, string | Date | null]>
        ).flatMap(([cropStart, cropEnd]) =>
          cropStart && cropEnd ?
            [
              {
                start: moment(cropStart).startOf("day"),
                end: moment(cropEnd).endOf("day"),
              },
            ]
          : [],
        )
      : [];

    while (cursor.isSameOrBefore(end, "month")) {
      const monthStart = cursor.clone().startOf("month");
      const monthEnd = cursor.clone().endOf("month");
      const nonIrrigated = this.isNonIrrigatedMonth(field, cursor);
      const selected =
        !nonIrrigated &&
        (field?.isperennialtree === true ?
          cursor.month() >= 2 && cursor.month() <= 9
        : annualCropWindows.some(
            (cropWindow) =>
              cropWindow.start.isSameOrBefore(monthEnd) &&
              cropWindow.end.isSameOrAfter(monthStart),
          ));

      months.push({
        key: cursor.format("YYYY-MM"),
        label: cursor.format("MMM YYYY"),
        selected,
        surfaceWater: selected ? 0 : null,
        groundWater: selected ? 100 : null,
      });
      cursor.add(1, "month");
    }

    return months;
  }

  private loadAppliedWaterInfo(field = this.selectedclaimedField): void {
    this.appliedWaterRequestSeq += 1;
    const requestSeq = this.appliedWaterRequestSeq;
    this.appliedWaterSourcesReadyFieldId = undefined;
    this.lastAppliedWaterPreviewKey = "";
    this.appliedWaterPreviewRequestSeq += 1;
    this.appliedWaterPreviewResult = null;
    this.isAppliedWaterPreviewLoading = false;
    this.destroyAppliedWaterChart();
    this.appliedWaterMonths = this.buildAppliedWaterMonths(field);
    this.appliedWaterDirty = false;
    this.isAppliedWaterLoading = false;

    if (
      !field ||
      !this.canViewAppliedWaterEstimate ||
      !this.isAppliedWaterEstimateMode
    ) {
      return;
    }

    this.isAppliedWaterLoading = true;
    this.dataService
      .getClaimedFieldIrrigatedInfo(field.id)
      .pipe(
        finalize(() => {
          if (requestSeq === this.appliedWaterRequestSeq) {
            this.isAppliedWaterLoading = false;
          }
        }),
      )
      .subscribe({
        next: (response) => {
          if (
            requestSeq !== this.appliedWaterRequestSeq ||
            this.selectedclaimedField?.id !== field.id
          ) {
            return;
          }

          const savedInfo = response?.irrigated_info ?? {};
          if (Object.keys(savedInfo).length > 0) {
            this.appliedWaterMonths = this.buildAppliedWaterMonths(field).map(
              (month) => {
                const savedMonth = savedInfo[month.key];
                // Match backend defaults for months with no saved input.
                if (!savedMonth) return month;
                if (
                  !savedMonth.irrigated ||
                  this.isNonIrrigatedMonth(field, moment(month.key, "YYYY-MM"))
                ) {
                  return {
                    ...month,
                    selected: false,
                    surfaceWater: null,
                    groundWater: null,
                  };
                }

                return {
                  ...month,
                  selected: true,
                  surfaceWater: savedMonth.surface_water,
                  groundWater: savedMonth.ground_water,
                };
              },
            );
          }
          this.appliedWaterSourcesReadyFieldId = field.id;
          this.sendAppliedWaterPreviewIfReady();
        },
        error: (error) => {
          if (requestSeq !== this.appliedWaterRequestSeq) return;
          console.error("Unable to load applied water information", error);
          this.snackbarService.show({
            message:
              "Unable to load saved water sources. Crop-based defaults are shown.",
          });
        },
      });
  }

  onAppliedWaterMonthSelected(month: AppliedWaterMonth, event: Event): void {
    if (this.type !== "grower") return;
    month.selected = (event.target as HTMLInputElement).checked;
    if (month.selected) {
      month.surfaceWater = 0;
      month.groundWater = 100;
    } else {
      month.surfaceWater = null;
      month.groundWater = null;
    }
    this.appliedWaterDirty = true;
  }

  onSurfaceWaterInput(month: AppliedWaterMonth, event: Event): void {
    if (this.type !== "grower") return;
    const input = event.target as HTMLInputElement;
    if (input.value === "") {
      month.selected = false;
      month.surfaceWater = null;
      month.groundWater = null;
      this.appliedWaterDirty = true;
      return;
    }

    const surfaceWater = Math.min(100, Math.max(0, input.valueAsNumber));
    month.surfaceWater = surfaceWater;
    month.groundWater = 100 - surfaceWater;
    input.value = String(surfaceWater);
    this.appliedWaterDirty = true;
  }

  saveAppliedWaterEstimate(): void {
    const claimedFieldId = this.selectedclaimedField?.id;
    if (
      this.type !== "grower" ||
      !this.appliedWaterDirty ||
      !claimedFieldId ||
      this.isAppliedWaterSaving
    ) {
      return;
    }

    const irrigatedInfo = this.buildIrrigatedInfoPayload();

    this.isAppliedWaterSaving = true;
    this.dataService
      .saveClaimedFieldIrrigatedInfo(claimedFieldId, irrigatedInfo)
      .pipe(finalize(() => (this.isAppliedWaterSaving = false)))
      .subscribe({
        next: () => {
          this.appliedWaterDirty = false;
          this.lastAppliedWaterPreviewKey = "";
          this.sendAppliedWaterPreviewIfReady();
          this.snackbarService.show({
            message: "Monthly water sources saved successfully.",
          });
        },
        error: (error) => {
          console.error("Unable to save applied water information", error);
          this.snackbarService.show({
            message: "Unable to save monthly water sources. Please try again.",
          });
        },
      });
  }

  private buildIrrigatedInfoPayload(): IrrigatedInfo {
    return this.appliedWaterMonths.reduce<IrrigatedInfo>((result, month) => {
      result[month.key] = {
        irrigated: month.selected,
        surface_water: month.selected ? (month.surfaceWater ?? 0) : 0,
        ground_water: month.selected ? (month.groundWater ?? 100) : 0,
      };
      return result;
    }, {});
  }

  private sendAppliedWaterPreviewIfReady(): void {
    const field = this.selectedclaimedField;
    if (
      !field ||
      this.appliedWaterSourcesReadyFieldId !== field.id ||
      !this.isAppliedWaterEstimateMode ||
      !Array.isArray(this.claimedFieldETs) ||
      this.claimedFieldETs.length === 0 ||
      this.appliedWaterDirty ||
      !this.canViewAppliedWaterEstimate
    ) {
      return;
    }

    const fieldWithMethods = field as ClaimedField &
      Record<string, string[] | null | undefined>;
    const cropDefinitions = [
      {
        crop_number: 1,
        crop_type: field.crop_type_01,
        start_date: field.ls_start_01,
        end_date: field.ls_end_01,
        irrigation_method:
          fieldWithMethods["irrigation_methods_01"]?.[0] ?? null,
      },
      {
        crop_number: 2,
        crop_type: field.crop_type_02,
        start_date: field.ls_start_02,
        end_date: field.ls_end_02,
        irrigation_method:
          fieldWithMethods["irrigation_methods_02"]?.[0] ?? null,
      },
      {
        crop_number: 3,
        crop_type: field.crop_type_03,
        start_date: field.ls_start_03,
        end_date: field.ls_end_03,
        irrigation_method:
          fieldWithMethods["irrigation_methods_03"]?.[0] ?? null,
      },
    ].filter((crop) =>
      Boolean(crop.crop_type),
    ) as AppliedWaterCalculationPayload["crops"];

    const payload: AppliedWaterCalculationPayload = {
      claimed_field_id: field.id,
      crops: cropDefinitions,
      monthly_irrigated: this.appliedWaterMonths.map((month) => month.selected),
      monthly_surface_water: this.appliedWaterMonths.map((month) =>
        month.selected ? (month.surfaceWater ?? 0) : 0,
      ),
      monthly_ground_water: this.appliedWaterMonths.map((month) =>
        month.selected ? (month.groundWater ?? 100) : 0,
      ),
      monthly_et: this.aggregateAppliedWaterSeriesByMonth("eta"),
      monthly_precipitation: this.aggregateAppliedWaterSeriesByMonth("prcp"),
    };
    const payloadKey = JSON.stringify(payload);
    if (payloadKey === this.lastAppliedWaterPreviewKey) return;
    this.lastAppliedWaterPreviewKey = payloadKey;
    const requestSeq = ++this.appliedWaterPreviewRequestSeq;
    this.isAppliedWaterPreviewLoading = true;

    this.dataService
      .previewAppliedWaterCalculation(field.id, payload)
      .pipe(
        finalize(() => {
          if (requestSeq === this.appliedWaterPreviewRequestSeq) {
            this.isAppliedWaterPreviewLoading = false;
          }
        }),
      )
      .subscribe({
        next: (response) => {
          if (
            requestSeq !== this.appliedWaterPreviewRequestSeq ||
            this.selectedclaimedField?.id !== field.id
          ) {
            return;
          }
          this.appliedWaterPreviewResult = response;
          this.renderAppliedWaterChart();
        },
        error: (error) => {
          if (this.lastAppliedWaterPreviewKey === payloadKey) {
            this.lastAppliedWaterPreviewKey = "";
          }
          console.error("Unable to send Applied Water preview payload", error);
        },
      });
  }

  private renderAppliedWaterChart(): void {
    if (!this.isAppliedWaterEstimateMode || !this.appliedWaterPreviewResult) {
      return;
    }

    if (this.appliedWaterRenderTimer) {
      clearTimeout(this.appliedWaterRenderTimer);
    }
    this.captureSeriesHiddenState(
      this.appliedWaterChart,
      this.appliedWaterSeriesHiddenState,
    );
    this.appliedWaterChart?.destroy();
    this.appliedWaterChart = undefined;

    this.appliedWaterRenderTimer = setTimeout(async () => {
      this.appliedWaterRenderTimer = undefined;
      const element = this.appliedWaterChartElement?.nativeElement;
      const result = this.appliedWaterPreviewResult;
      if (!element || !result || !this.isAppliedWaterEstimateMode) {
        return;
      }

      const unitLabel = this.displayUnitLabel();
      const convertSeries = (values: number[]): number[] =>
        values.map((value) => this.toDisplayUnit(value) ?? 0);
      const toMonthlyPoints = (values: number[]) =>
        convertSeries(values).map((value, index) => ({
          x: moment(result.months[index], "YYYY-MM").valueOf(),
          y: value,
        }));
      const series = [
        {
          name: "Monthly Groundwater Applied - Medium",
          type: "bar",
          data: toMonthlyPoints(result.groundWaterAppliedMed),
        },
        {
          name: "Monthly Groundwater Applied - High",
          type: "bar",
          data: toMonthlyPoints(result.groundWaterAppliedHigh),
        },
        {
          name: "Monthly Groundwater Applied - Low",
          type: "bar",
          data: toMonthlyPoints(result.groundWaterAppliedLow),
        },
        {
          name: "Monthly Surface Water Applied",
          type: "bar",
          data: toMonthlyPoints(result.surfaceWaterAppliedMed),
        },
        {
          name: "Groundwater Applied - Medium",
          type: "line",
          data: toMonthlyPoints(result.cumulativeGroundWaterAppliedMed),
        },
        {
          name: "Groundwater Applied - High",
          type: "line",
          data: toMonthlyPoints(result.cumulativeGroundWaterAppliedHigh),
        },
        {
          name: "Groundwater Applied - Low",
          type: "line",
          data: toMonthlyPoints(result.cumulativeGroundWaterAppliedLow),
        },
        {
          name: "Surface Water Applied",
          type: "line",
          data: toMonthlyPoints(result.cumulativeSurfaceWaterAppliedMed),
        },
      ];
      if (this.selectedclaimedField?.total_target_type === "Applied Water") {
        const field = this.selectedclaimedField;
        const targetInches =
          field.total_target_water_unit === "Feet" ?
            field.total_target_water * 12
          : field.total_target_water;
        series.push({
          name: "Target Water",
          type: "line",
          data: toMonthlyPoints(result.months.map(() => targetInches)),
        });
      }
      const colors = [
        "#F97316",
        "rgba(0, 0, 0, 0)",
        "rgba(0, 0, 0, 0)",
        "#DB2777",
        "#0EA5E9",
        "#1D4ED8",
        "#7C3AED",
        "#10B981",
        "#C27803",
      ];
      // ApexCharts recreates legend elements when series visibility changes.
      // Keep monthly/total pairs ordered without changing the stacked series.
      const applyLegendOrder = () => {
        const legendOrderBySeriesIndex = [0, 2, 4, 6, 1, 3, 5, 7, 8];
        element
          .querySelectorAll<HTMLElement>(".apexcharts-legend-series")
          .forEach((legendItem) => {
            const seriesIndex = Number(legendItem.getAttribute("rel")) - 1;
            const order = legendOrderBySeriesIndex[seriesIndex];
            if (order !== undefined) legendItem.style.order = String(order);
          });
      };
      this.appliedWaterChart = new ApexCharts(element, {
        chart: {
          type: "line",
          stacked: true,
          stackOnlyBar: true,
          events: {
            mounted: applyLegendOrder,
            updated: applyLegendOrder,
          },
          height: "100%",
          toolbar: {
            show: false,
            autoSelected: "zoom",
          },
          zoom: { enabled: true, type: "x", autoScaleYaxis: false },
          animations: { enabled: true },
        },
        series,
        colors,
        stroke: {
          curve: "smooth",
          width: [0, 0, 0, 0, 3, 3, 3, 3, 3],
          dashArray: series.map((item) =>
            item.name === "Target Water" ? 5 : 0,
          ),
        },
        markers: {
          size: [0, 0, 0, 0, 4, 4, 4, 4, 0],
          hover: { size: 6 },
        },
        plotOptions: {
          bar: { columnWidth: "55%", borderRadius: 3 },
        },
        dataLabels: { enabled: false },
        xaxis: {
          crosshairs: { show: false },
          type: "datetime",
          labels: { datetimeUTC: false, format: "MMM yyyy" },
          title: { text: "Month" },
        },
        yaxis: {
          min: 0,
          title: { text: `Applied Water (${unitLabel})` },
          labels: {
            formatter: (value: number) => value.toFixed(2),
          },
        },
        tooltip: {
          shared: true,
          intersect: false,
          followCursor: true,
          fixed: { enabled: false },
          x: { format: "MMM yyyy" },
          y: {
            formatter: (value: number) =>
              `${value.toFixed(2)} ${this.displayUnitLabel(true)}`,
          },
        },
        legend: {
          position: "top",
          horizontalAlign: "center",
        },
        grid: { borderColor: this.mode === "dark" ? "#374151" : "#E5E7EB" },
        noData: { text: "No applied water data available" },
      });
      const chart = this.appliedWaterChart;
      try {
        await chart.render();
        if (this.appliedWaterChart !== chart) return;
        this.appliedWaterSeriesHiddenState.forEach((hidden, seriesName) => {
          if (hidden && series.some((item) => item.name === seriesName)) {
            chart.hideSeries(seriesName);
          }
        });
        applyLegendOrder();
      } catch (error) {
        if (this.appliedWaterChart === chart) {
          console.error("Unable to render Applied Water chart", error);
        }
      }
    });
  }

  resetAppliedWaterChart(): void {
    // ApexCharts resetSeries() triggers its SVG drag handlers from passive
    // listeners after a zoom, producing preventDefault console errors.
    // Rebuilding from the unchanged response restores the full month range
    // without entering that faulty reset path.
    this.renderAppliedWaterChart();
  }

  private destroyAppliedWaterChart(): void {
    if (this.appliedWaterRenderTimer) {
      clearTimeout(this.appliedWaterRenderTimer);
      this.appliedWaterRenderTimer = undefined;
    }
    this.captureSeriesHiddenState(
      this.appliedWaterChart,
      this.appliedWaterSeriesHiddenState,
    );
    this.appliedWaterChart?.destroy();
    this.appliedWaterChart = undefined;
  }

  private aggregateAppliedWaterSeriesByMonth(
    valueField: "eta" | "prcp",
  ): number[] {
    const monthlyTotals = new Array<number>(12).fill(0);
    const monthIndexes = new Map(
      this.appliedWaterMonths.map((month, index) => [month.key, index]),
    );

    for (const item of this.claimedFieldETs ?? []) {
      const monthIndex = monthIndexes.get(
        moment(item.et_date).format("YYYY-MM"),
      );
      const value = Number(item[valueField]);
      if (monthIndex === undefined || !Number.isFinite(value)) continue;
      monthlyTotals[monthIndex] += value;
    }

    return monthlyTotals.map((total) => Number(total.toFixed(4)));
  }

  private reinitializeMainChart(): void {
    if (!this.chartElement?.nativeElement) return;
    this.chart?.destroy();
    this.chart = new ApexCharts(
      this.chartElement.nativeElement,
      this.chartOptions,
    );
    this.chart.render();
  }

  // Control the toggle state of time filter
  toggleDropdown() {
    this.isOpen = !this.isOpen;
  }
  // Close the time filter
  closeDropdown() {
    this.isOpen = false;
  }

  toggleFullscreen() {
    const el = this.chartContainer.nativeElement as HTMLElement;
    if (!document.fullscreenElement) {
      el.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
    if (!this.fullscreenListener) {
      this.fullscreenListener = this.renderer.listen(
        "document",
        "fullscreenchange",
        () => {
          this.isFullscreen = !!document.fullscreenElement;
          this.updateYAxisTickAmount();
        },
      );
    }
  }

  private updateYAxisTickAmount(): void {
    const tickAmount = this.isFullscreen ? 10 : 5;
    const activeChart = this.isPerennialTreeMode ? this.chart : this.chart;
    const currentYaxis = activeChart?.w?.config?.yaxis;
    if (!currentYaxis) return;
    const updated =
      Array.isArray(currentYaxis) ?
        currentYaxis.map((y: any) => ({ ...y, tickAmount }))
      : { ...currentYaxis, tickAmount };
    activeChart.updateOptions({ yaxis: updated }, false, false);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If fields list changes, reset the selectedRow and calculate items per page.
    if (
      changes["claimedFieldsForChart"] &&
      changes["claimedFieldsForChart"].currentValue
    ) {
      this.claimedFields = this.claimedFieldsForChart;
      // Get Current Login User Id then fetch the claimedFields
      this.generateChart();
    }
  }

  /**
   * Function of ngOnInit Lifecycle Hook
   */
  ngOnInit(): void {
    // It can make sure when TimeRange Dropdown is openning, click other area could close the Dropdown.
    this.clickListener = this.renderer.listen(
      "document",
      "click",
      (event: Event) => {
        const targetElement = event.target as HTMLElement;
        if (
          !this.dropdownButton.nativeElement.contains(targetElement) &&
          !this.dropdownMenu.nativeElement.contains(targetElement)
        ) {
          this.closeDropdown();
        }
      },
    );

    // Subscribe to the selectedUserFieldId observable
    this.userFieldService.selectedUserFieldId$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((id: number | null) => {
        if (id && this.chart) {
          this.selectedclaimedField = this.claimedFields?.find(
            (userfield) => userfield.id === id,
          );
          if (!this.selectedclaimedField) {
            console.error("Selected claimed field not found");
            return;
          }
          if (
            this.isAppliedWaterEstimateMode &&
            !this.canViewAppliedWaterEstimate
          ) {
            this.isAppliedWaterEstimateMode = false;
            this.isWeatherMode = false;
          }
          this.loadAppliedWaterInfo(this.selectedclaimedField);
          // Fields without acres can't display acre-feet — force inches.
          if (!this.selectedclaimedField.acres) {
            this.displayUnit.set("inches");
          }
          // ET values are loaded on demand for the selected field.
          this.claimedFieldETs = undefined;
          const shouldRefreshWeather = this.isWeatherMode;
          if (shouldRefreshWeather) {
            // Clear stale weather data; refill after API returns.
            this.fieldWeather = null;
            this.fieldWeatherFieldId = undefined;
          }
          // Initialize the isPerennialTreeMode to false when re-select a claimed field
          this.isPerennialTreeMode = false;
          this.previousPerennailTreeMode = false;
          // Also reassign the title, isPerennailTree of selectedclaimedField
          this.title = this.selectedclaimedField?.field_name;
          this.isPerennailTree = this.selectedclaimedField?.isperennialtree;
          // Preserve current legend state before rebuilding chart for the next field.
          this.captureSeriesHiddenState(
            this.chart,
            this.isWeatherMode ?
              this.weatherSeriesHiddenState
            : this.etSeriesHiddenState,
          );
          this.reinitializeMainChart();
          this.loadEtForSelectedField(true, shouldRefreshWeather);
        }
      });

    // Subscribe the mode (dark or light) and pass to Map (Mapbox)
    this.themeService.darkMode$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isDarkMode) => {
        this.mode = isDarkMode === true ? "dark" : "light";
      });
  }

  /**
   * Function for fetching claimed fields for a certain manager
   * @param userId
   */
  generateChart(): void {
    // Initialize the Main Chart based on this.chartOptions then render to update
    this.chart = new ApexCharts(
      this.chartElement.nativeElement,
      this.chartOptions,
    );
    this.chart.render(); // this.chart.hideSeries('Precipitation'); this.chart.hideSeries('Accumulated ET value');
    // Set the first claimedField as the local variable - selectedclaimedField
    this.selectedclaimedField = this.claimedFields?.[0];
    this.loadAppliedWaterInfo(this.selectedclaimedField);
    // Fields without acres can't display acre-feet — force inches.
    if (this.selectedclaimedField && !this.selectedclaimedField.acres) {
      this.displayUnit.set("inches");
    }
    // ET values are loaded on demand for the selected field.
    this.claimedFieldETs = undefined;
    // Extract the first claimedField's field_name as the local variable - title
    this.title = this.selectedclaimedField?.field_name;
    // record the isPerennailTree state, as later it could be used for checking the previous 'state'
    this.isPerennailTree = this.selectedclaimedField?.isperennialtree;

    // Update the Charts when the selected field's ET values are ready.
    this.loadEtForSelectedField(false, this.isWeatherMode);
  }

  private normalizeEtValuesResponse(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.userFieldEtvalues)) {
      return response.userFieldEtvalues;
    }
    return [];
  }

  private applyEtValuesToSelectedField(etValues: any[]): void {
    this.claimedFieldETs = etValues;
    if (this.selectedclaimedField) {
      this.selectedclaimedField.userFieldEtvalues = etValues;
    }
    this.sendAppliedWaterPreviewIfReady();
  }

  private loadEtForSelectedField(
    isModeChange: boolean,
    loadWeatherAfterEt = false,
  ): void {
    if (!this.selectedclaimedField) return;

    const selectedFieldId = this.selectedclaimedField.id;
    const cachedEtValues = this.etDataCache.get(selectedFieldId);
    if (cachedEtValues) {
      this.applyEtValuesToSelectedField(cachedEtValues);
      if (loadWeatherAfterEt) {
        this.loadWeatherForSelectedField(isModeChange);
      } else {
        this.updateCharts(isModeChange);
      }
      return;
    }

    const existingEtValues = this.selectedclaimedField.userFieldEtvalues;
    if (Array.isArray(existingEtValues) && existingEtValues.length > 0) {
      this.etDataCache.set(selectedFieldId, existingEtValues);
      this.applyEtValuesToSelectedField(existingEtValues);
      if (loadWeatherAfterEt) {
        this.loadWeatherForSelectedField(isModeChange);
      } else {
        this.updateCharts(isModeChange);
      }
      return;
    }

    const requestSeq = ++this.etFieldSwitchRequestSeq;
    this.isEtFieldSwitchLoading = true;
    this.dataService
      .getClaimedFieldEtValues(selectedFieldId)
      .pipe(
        finalize(() => {
          if (requestSeq === this.etFieldSwitchRequestSeq) {
            this.isEtFieldSwitchLoading = false;
          }
        }),
      )
      .subscribe({
        next: (response: any) => {
          if (
            requestSeq !== this.etFieldSwitchRequestSeq ||
            this.selectedclaimedField?.id !== selectedFieldId
          ) {
            return;
          }
          const etValues = this.normalizeEtValuesResponse(response);
          this.etDataCache.set(selectedFieldId, etValues);
          this.applyEtValuesToSelectedField(etValues);
          if (loadWeatherAfterEt) {
            this.loadWeatherForSelectedField(isModeChange);
          } else {
            this.updateCharts(isModeChange);
          }
        },
        error: (error: any) => {
          if (requestSeq !== this.etFieldSwitchRequestSeq) return;
          this.applyEtValuesToSelectedField([]);
          if (loadWeatherAfterEt) {
            this.loadWeatherForSelectedField(isModeChange);
          } else {
            this.updateCharts(isModeChange);
          }
          console.error("Cannot Get ET Data for the current Field", error);
        },
      });
  }

  private loadWeatherForSelectedField(isModeChange: boolean): void {
    if (!this.selectedclaimedField) return;

    if (
      this.fieldWeather &&
      this.fieldWeatherFieldId === this.selectedclaimedField.id
    ) {
      this.updateCharts(isModeChange);
      return;
    }

    const lat = this.selectedclaimedField.centroid_latlong[0];
    const long = this.selectedclaimedField.centroid_latlong[1];
    const stationId = this.selectedclaimedField.cimis_id_historical;
    const stationIdForecast = this.selectedclaimedField.cimis_id_historical;
    const cacheKey = this.getWeatherCacheKey(this.selectedclaimedField);
    const cachedWeather = this.weatherDataCache.get(cacheKey);
    if (cachedWeather) {
      this.fieldWeather = cachedWeather;
      this.fieldWeatherFieldId = this.selectedclaimedField.id;
      this.updateCharts(isModeChange);
      return;
    }
    const requestSeq = ++this.weatherFieldSwitchRequestSeq;

    this.isWeatherFieldSwitchLoading = true;
    this.dataService
      .getFieldWeather(lat, long, stationId, stationIdForecast)
      .pipe(
        finalize(() => {
          if (requestSeq === this.weatherFieldSwitchRequestSeq) {
            this.isWeatherFieldSwitchLoading = false;
          }
        }),
      )
      .subscribe({
        next: (fieldWeather: any) => {
          if (requestSeq !== this.weatherFieldSwitchRequestSeq) return;
          this.weatherDataCache.set(cacheKey, fieldWeather);
          this.fieldWeather = fieldWeather;
          this.fieldWeatherFieldId = this.selectedclaimedField?.id;
          this.updateCharts(isModeChange);
        },
        error: (error: any) => {
          if (requestSeq !== this.weatherFieldSwitchRequestSeq) return;
          console.error("Cannot Get Weather Data for the current Field", error);
        },
      });
  }

  private getWeatherCacheKey(field: any): string {
    return (
      field.cimis_id_historical ||
      `${field.centroid_latlong?.[0]}_${field.centroid_latlong?.[1]}`
    );
  }

  private async getWeatherDataForField(field: any): Promise<any> {
    const cacheKey = this.getWeatherCacheKey(field);
    const cachedWeather = this.weatherDataCache.get(cacheKey);
    if (cachedWeather) return cachedWeather;

    const fieldWeather = await firstValueFrom(
      this.dataService.getFieldWeather(
        field.centroid_latlong[0],
        field.centroid_latlong[1],
        field.cimis_id_historical,
        field.cimis_id,
      ),
    );
    this.weatherDataCache.set(cacheKey, fieldWeather);
    return fieldWeather;
  }

  private async ensureEtValuesForField(field: any): Promise<any[]> {
    if (!field?.id) return [];

    const cachedEtValues = this.etDataCache.get(field.id);
    if (cachedEtValues) {
      field.userFieldEtvalues = cachedEtValues;
      return cachedEtValues;
    }

    if (
      Array.isArray(field.userFieldEtvalues) &&
      field.userFieldEtvalues.length > 0
    ) {
      this.etDataCache.set(field.id, field.userFieldEtvalues);
      return field.userFieldEtvalues;
    }

    const etValues = this.normalizeEtValuesResponse(
      await firstValueFrom(this.dataService.getClaimedFieldEtValues(field.id)),
    );
    this.etDataCache.set(field.id, etValues);
    field.userFieldEtvalues = etValues;
    if (this.selectedclaimedField?.id === field.id) {
      this.applyEtValuesToSelectedField(etValues);
    }
    return etValues;
  }

  private async getAllClaimedFieldsWithEtForDownload(): Promise<any[]> {
    if (this.type === "manager") {
      return firstValueFrom(this.dataService.getClaimedFieldsManagerWithEt());
    }
    if (this.type === "entity") {
      return firstValueFrom(this.dataService.getClaimedFieldsEntityWithEt());
    }
    return firstValueFrom(
      this.dataService.getClaimedFieldsWithEt(this.authService.getUserId()),
    );
  }

  private cacheDownloadedEtValues(fields: any[]): void {
    fields.forEach((field) => {
      if (field?.id && Array.isArray(field.userFieldEtvalues)) {
        this.etDataCache.set(field.id, field.userFieldEtvalues);
        if (this.selectedclaimedField?.id === field.id) {
          this.applyEtValuesToSelectedField(field.userFieldEtvalues);
        }
      }
    });
  }

  /**
   * Captures the rendered height of the chart container and sets it as a fixed min-height
   * so toggling between chart modes does not cause layout shifts.
   */
  private lockChartContainerHeight(): void {
    if (this.chartContainerHeightLocked) return;
    const el = this.chartContainerElement?.nativeElement;
    if (el && el.offsetHeight > 0) {
      el.style.maxHeight = `${el.offsetHeight}px`;
      this.chartContainerHeightLocked = true;
    }
  }

  /**
   * Function for generating annotations for X dynamically based on crop_type (and dates)
   * @returns annotation array
   */
  configCropAnnotations() {
    // Define an empty array (result)
    const annotations: any[] = [];
    [
      [
        this.selectedclaimedField?.crop_type_01,
        this.selectedclaimedField?.ls_start_01,
      ],
      [
        this.selectedclaimedField?.crop_type_01,
        this.selectedclaimedField?.ls_end_01,
      ],
      [
        this.selectedclaimedField?.crop_type_02,
        this.selectedclaimedField?.ls_start_02,
      ],
      [
        this.selectedclaimedField?.crop_type_02,
        this.selectedclaimedField?.ls_end_02,
      ],
      [
        this.selectedclaimedField?.crop_type_03,
        this.selectedclaimedField?.ls_start_03,
      ],
      [
        this.selectedclaimedField?.crop_type_03,
        this.selectedclaimedField?.ls_end_03,
      ],
    ].forEach((value, index) => {
      // Loop crop 123 and start & end dates 123
      // if value[1] (date) is falsy, skip...
      if (!value[1]) return;
      // transform the date to string YYYY-MM-DD
      let dateStr = moment(value[1]).utc().format("YYYY-MM-DD");
      // Using different renders for start and end dates
      if (index % 2 === 0) {
        // start date (plant date)
        annotations.push({
          x: dateStr, // Replace with your desired date/time or index
          strokeDashArray: 5,
          borderWidth: 3,
          borderColor: "#0E9F6E",
          label: {
            borderColor: "#0E9F6E",
            style: {
              color: "#D1D5DB",
              background: "#0E9F6E",
              fontSize: "13px",
              fontWeight: "bold",
            },
            orientation: "horizontal",
            text: `${value[0]}`,
          },
        });
      } else {
        // end date (harvest date)
        annotations.push({
          x: dateStr, // Replace with your desired date/time or index
          strokeDashArray: 5,
          borderWidth: 3,
          borderColor: "#C27803",
          label: {
            borderColor: "#C27803",
            style: {
              color: "#D1D5DB",
              background: "#C27803",
              fontSize: "13px",
              fontWeight: "bold",
            },
            orientation: "horizontal",
            text: `${value[0]}`, // text: `${value[0].split(' ')[0]}`
          },
        });
      }
    });
    return annotations;
  }

  private buildFutureDates(lastDate: string, days: number): string[] {
    const base =
      moment(lastDate, "YYYY-MM-DD", true).isValid() ?
        moment(lastDate)
      : moment(this.currentDate.format("YYYY-MM-DD"));
    return Array.from({ length: days }, (_, i) =>
      base
        .clone()
        .add(i + 1, "day")
        .format("YYYY-MM-DD"),
    );
  }

  private buildDatesThroughWeatherCurrentDate(lastDate: string): string[] {
    const base =
      moment(lastDate, "YYYY-MM-DD", true).isValid() ?
        moment(lastDate)
      : moment(
          startDateCalendarWaterYear().subtract(1, "day").format("YYYY-MM-DD"),
        );
    const current = moment(
      (this.todayDate ?? this.currentDate).format("YYYY-MM-DD"),
    );
    const gapDays = Math.max(0, current.diff(base, "days"));
    return this.buildFutureDates(base.format("YYYY-MM-DD"), gapDays);
  }

  get isEtDataUnavailable(): boolean {
    return (
      !!this.selectedclaimedField &&
      Array.isArray(this.claimedFieldETs) &&
      !this.claimedFieldETs.some(
        (row: any) =>
          row.et_date >= this.etDisplayStart.format("YYYY-MM-DD") &&
          row.et_date <= this.currentDate.format("YYYY-MM-DD") &&
          typeof row.eta === "number" &&
          Number.isFinite(row.eta),
      )
    );
  }

  updateCharts(isModeChange: boolean) {
    // Update the Main Chart, for both Perennial (Close the isPerennialTreeMode) and Annual Crops
    if (!this.isPerennialTreeMode) {
      let claimedFieldETsSubset = this.claimedFieldETs;
      if (!this.selectedclaimedField) {
        console.error("No selected claimed field found for updating chart.");
        return;
      }
      if (!Array.isArray(claimedFieldETsSubset)) {
        return;
      }
      // Weather follows the calendar water year; ET displays follow the two-day lag.
      const displayStart =
        this.isWeatherMode ? startDateCalendarWaterYear() : this.etDisplayStart;
      claimedFieldETsSubset = claimedFieldETsSubset.filter(
        (claimedFieldET: any) =>
          claimedFieldET.et_date >= displayStart.format("YYYY-MM-DD") &&
          claimedFieldET.et_date <= this.currentDate.format("YYYY-MM-DD"),
      );
      // Extract and prepare the dateList, precipitation, cumulated precipitation, for updating chart later
      // Define the precipitation
      const precipitation =
        claimedFieldETsSubset
          .map((claimedFieldET: { prcp: number }) =>
            this.toDisplayUnit(
              claimedFieldET.prcp < 0.02 ? null : claimedFieldET.prcp,
            ),
          )
          ?.slice(-this.timeRangeFilter[this.timeRange]) ?? [];
      // Define the cumulated precipitation
      const cprecipitation =
        claimedFieldETsSubset
          .map((claimedFieldET: { cprcp: number }) =>
            this.toDisplayUnit(claimedFieldET.cprcp),
          )
          ?.slice(-this.timeRangeFilter[this.timeRange]) ?? [];
      // Date List - first date of water to the latest date (2 days ago)
      const dateList =
        claimedFieldETsSubset
          .map((claimedFieldET: { et_date: string }) => claimedFieldET.et_date)
          ?.slice(-this.timeRangeFilter[this.timeRange]) ?? [];
      // Extend the weather timeline to today's Pacific-date so observed weather reaches the chart marker.
      const dateListToToday = [
        ...dateList,
        ...this.buildDatesThroughWeatherCurrentDate(
          dateList[dateList.length - 1],
        ),
      ];
      // Weather mode: render a dedicated dummy chart; ET mode remains unchanged below.
      if (this.isWeatherMode) {
        const annotations = this.configCropAnnotations();
        const todayDateStr = this.todayDate.format("YYYY-MM-DD");
        const forecastMinDaily =
          this.fieldWeather?.forecast?.daily?.temperature_2m_min ?? [];
        const forecastMaxDaily =
          this.fieldWeather?.forecast?.daily?.temperature_2m_max ?? [];
        const historicalAvgMin = this.fieldWeather?.historicalAvg?.min ?? [];
        const historicalAvgMax = this.fieldWeather?.historicalAvg?.max ?? [];
        const forecastHorizon = Math.max(
          forecastMinDaily.length,
          forecastMaxDaily.length,
          historicalAvgMin.length,
          historicalAvgMax.length,
        );
        const futureDateCount = Math.max(0, forecastHorizon - 1);
        const bridgeDaysCount = Math.max(
          0,
          dateListToToday.length - dateList.length,
        );
        // Generate weatherDate List
        const futureDates = this.buildFutureDates(
          dateListToToday[dateListToToday.length - 1],
          futureDateCount,
        );
        const weatherDateList =
          [...dateListToToday, ...futureDates]?.slice(
            -this.timeRangeFilter[this.timeRange],
          ) ?? [];
        const alignWeatherSeries = (
          values: any[] = [],
          dates: string[] = [],
        ) => {
          const byDate = new Map(
            dates.map((date, index) => [date, values[index]]),
          );
          return weatherDateList.map((date) => byDate.get(date) ?? null);
        };
        const forecastDates = Array.from(
          { length: forecastHorizon },
          (_, index) =>
            moment(todayDateStr).add(index, "day").format("YYYY-MM-DD"),
        );
        const alignSeriesToCurrentDate = (seriesData: any[] = []) =>
          alignWeatherSeries(seriesData, forecastDates);
        const observedDates: string[] =
          this.fieldWeather?.historical?.daily?.time ?? [];
        // Generate MinTem and MaxTem Lists
        const minObserved =
          this.fieldWeather?.historical?.daily?.temperature_2m_min ?? [];
        const maxObserved =
          this.fieldWeather?.historical?.daily?.temperature_2m_max ?? [];
        const minTem = alignWeatherSeries(minObserved, observedDates);
        const maxTem = alignWeatherSeries(maxObserved, observedDates);
        // Generate forecastMinTem and forecastMaxTem Lists
        const forecastMinTem = alignSeriesToCurrentDate(forecastMinDaily);
        const forecastMaxTem = alignSeriesToCurrentDate(forecastMaxDaily);
        // Generate forecastMinTemAvg and forecastMaxTemAvg
        const forecastMinTemAvg = alignSeriesToCurrentDate(historicalAvgMin);
        const forecastMaxTemAvg = alignSeriesToCurrentDate(historicalAvgMax);
        // Generate GDD and Chilling Hours Lists
        const gddObserved =
          this.fieldWeather?.historical?.daily?.gdd_list ?? [];
        const gdd = alignWeatherSeries(gddObserved, observedDates);
        const chill = alignWeatherSeries(
          this.fieldWeather?.chilling_hours ?? [],
          observedDates,
        );

        // Generate Precipitation and Culumative Precipitation Lists
        const precipitationExtended =
          [
            ...precipitation,
            ...new Array(bridgeDaysCount + futureDateCount).fill(null),
          ]?.slice(-this.timeRangeFilter[this.timeRange]) ?? [];
        const cprecipitationExtended =
          [
            ...cprecipitation,
            ...new Array(bridgeDaysCount).fill(
              cprecipitation[cprecipitation.length - 1],
            ),
            ...new Array(futureDateCount).fill(null),
          ]?.slice(-this.timeRangeFilter[this.timeRange]) ?? [];
        // Rebuild weather series while restoring prior legend visibility from weather map.
        const weatherSeries = this.applyPersistedSeriesHiddenState(
          [
            {
              name: "Min Temperature",
              data: minTem,
              color: "#FACC15",
              type: "area",
            },
            {
              name: "Forecasted MinT",
              data: forecastMinTem,
              color: "#C27803",
              type: "area",
            },
            {
              name: "Historical Avg MinT",
              data: forecastMinTemAvg,
              color: "#0E9F6E",
              type: "area",
            },
            {
              name: "Max Temperature",
              data: maxTem,
              color: "#FB7185",
              type: "area",
            },
            {
              name: "Forecasted MaxT",
              data: forecastMaxTem,
              color: "#EF4444",
              type: "area",
            },
            {
              name: "Historical Avg MaxT",
              data: forecastMaxTemAvg,
              color: "#3F83F8",
              type: "area",
            },
            {
              name: "Daily Precipitation",
              data: precipitationExtended,
              color: "#06b6d4",
              type: "column",
            },
            {
              name: "Cumulative Precipitation",
              data: cprecipitationExtended,
              color: "#1A56DB",
              type: "area",
              hidden: true,
            },
            {
              name: "Growing Degree Days",
              data: gdd,
              color: "#6875F5",
              type: "area",
              hidden: true,
            },
            {
              name: "Chilling Portions",
              data: chill,
              color: "#E74694",
              type: "area",
              hidden: true,
            },
          ].map((series) => ({
            ...series,
            // ApexCharts only shares tooltips when every series has equal length.
            data: weatherDateList.map((_, index) => series.data[index] ?? null),
          })),
          this.weatherSeriesHiddenState,
        );
        // Update the Weather Chart
        const priorWeatherEvents = this.chartOptions.chart?.events ?? {};
        const priorWeatherMounted = priorWeatherEvents.mounted;
        this.chart?.updateOptions({
          chart: {
            events: {
              ...priorWeatherEvents,
              mounted: (ctx: any, config: any) => {
                priorWeatherMounted?.(ctx, config);
                this.bindWeatherLegendPopover(ctx, config);
              },
              updated: this.bindWeatherLegendPopover,
            },
          },
          grid: {
            padding: {
              left: 60,
              right: 80,
            },
          },
          series: weatherSeries,
          fill: {
            type: "gradient",
            gradient: {
              shadeIntensity: 0.2,
              opacityFrom: 0.3, // lighter start
              opacityTo: 0.02, // almost transparent end
              stops: [0, 90, 100],
            },
          },
          tooltip: {
            enabled: true,
            shared: true,
            intersect: false,
            followCursor: true,
            fixed: { enabled: false },
            // Zero is valid weather data (e.g. no rainfall or chilling yet).
            hideEmptySeries: false,
            x: {
              show: false,
            },
            y: {
              formatter: (value: number, opts?: any) => {
                const seriesName =
                  opts?.w?.config?.series?.[opts.seriesIndex]?.name;
                const rawValue =
                  opts?.w?.config?.series?.[opts.seriesIndex]?.data?.[
                    opts.dataPointIndex
                  ];
                const category =
                  opts?.w?.config?.xaxis?.categories?.[opts.dataPointIndex] ??
                  opts?.w?.globals?.categoryLabels?.[opts.dataPointIndex];
                if (rawValue === null || rawValue === undefined) {
                  if (seriesName === "Daily Precipitation") {
                    // Small rainfall values are hidden in the plot, but the
                    // tooltip should distinguish those records from missing data.
                    const record = claimedFieldETsSubset.find(
                      (row: any) => row.et_date === category,
                    );
                    if (
                      typeof record?.prcp === "number" &&
                      Number.isFinite(record.prcp)
                    ) {
                      const amount = this.toDisplayUnit(record.prcp);
                      return amount == null ? "N/A" : amount.toFixed(2);
                    }
                  }
                  return "N/A";
                }
                const numericValue = Number(rawValue);
                return Number.isFinite(numericValue) ?
                    numericValue.toFixed(2)
                  : "N/A";
              },
            },
          },
          stroke: {
            width: [3, 3, 2.5, 3, 3, 2.5, 2, 2, 2, 2],
            curve: "smooth",
            dashArray: [0, 10, 3, 0, 10, 3, 0, 0, 0, 0],
            lineCap: "round",
          },
          xaxis: {
            crosshairs: { show: false },
            categories: weatherDateList,
            tickAmount: 12,
            formatter: function (value: number) {
              return value;
            },
          },
          yaxis: [
            {
              seriesName: [
                "Min Temperature",
                "Forecasted MinT",
                "Historical Avg MinT",
                "Max Temperature",
                "Forecasted MaxT",
                "Historical Avg MaxT",
              ],
              opposite: false,
              min: 0,
              title: {
                text: "Temperature (°F)",
                style: {
                  cssClass:
                    "text-sm font-medium fill-gray-500 dark:fill-gray-400",
                },
              },
              show: true,
              labels: {
                show: true,
                style: {
                  fontFamily: "Inter, sans-serif",
                  cssClass:
                    "text-xs font-normal fill-gray-500 dark:fill-gray-400",
                },
                formatter: function (value: number) {
                  const n = Number(value);
                  return Number.isFinite(n) ? n.toFixed(2) : "";
                },
              },
            },
            {
              seriesName: ["Daily Precipitation"],
              opposite: true,
              min: 0,
              title: {
                text: `Daily Precip (${this.displayUnitLabel()})`,
                style: {
                  cssClass: "text-sm font-normal",
                  color: "#06b6d4",
                },
              },
              show: true,
              labels: {
                show: true,
                style: {
                  fontFamily: "Inter, sans-serif",
                  cssClass: "text-xs font-normal",
                  colors: ["#06b6d4"],
                },
                formatter: function (value: number) {
                  const n = Number(value);
                  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
                },
              },
            },
            {
              seriesName: ["Cumulative Precip (Inches)"],
              opposite: true,
              min: 0,
              title: {
                text: `Cumulative Precip (${this.displayUnitLabel()})`,
                style: {
                  cssClass: "text-sm font-normal",
                  color: "#1A56DB",
                },
              },
              show: true,
              labels: {
                show: true,
                style: {
                  fontFamily: "Inter, sans-serif",
                  cssClass: "text-xs font-normal",
                  colors: ["#1A56DB"],
                },
                formatter: function (value: number) {
                  const n = Number(value);
                  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
                },
              },
            },
            {
              seriesName: ["Growing Degree Days"],
              opposite: true,
              min: 0,
              title: {
                text: "Growing Degree Days (Day)",
                style: {
                  cssClass: "text-sm font-normal",
                  color: "#6875F5",
                },
              },
              show: true,
              labels: {
                show: true,
                style: {
                  fontFamily: "Inter, sans-serif",
                  cssClass: "text-xs font-normal",
                  colors: ["#6875F5"],
                },
                formatter: function (value: number) {
                  const n = Number(value);
                  return Number.isFinite(n) ? Math.round(n).toString() : "0";
                },
              },
            },
            {
              seriesName: ["Chilling Portions"],
              opposite: true,
              min: 0,
              title: {
                text: "Chilling Portions (Chilling Portion)",
                style: {
                  cssClass: "text-sm font-normal",
                  color: "#E74694",
                },
              },
              show: true,
              labels: {
                show: true,
                style: {
                  fontFamily: "Inter, sans-serif",
                  cssClass: "text-xs font-normal",
                  colors: ["#E74694"],
                },
                formatter: function (value: number) {
                  const n = Number(value);
                  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
                },
              },
            },
          ],
          legend: {
            show: true,
            width: 900,
            position: "top",
            horizontalAlign: "center",
            labels: {
              colors: "#64748b",
            },
            clusterGroupedSeries: false,
          },
          annotations: {
            xaxis: [
              ...annotations,
              {
                x: `${this.todayDate.format("YYYY-MM-DD")}`,
                strokeDashArray: 5,
                borderWidth: 3,
                borderColor: "#7E3BF2",
                label: {
                  borderColor: "#7E3BF2",
                  style: {
                    color: "#D1D5DB",
                    background: "#7E3BF2",
                  },
                  orientation: "horizontal",
                  text: `Current Date: ${this.todayDate.format("YYYY-MM-DD")}`,
                },
              },
            ],
          },
          plotOptions: {
            bar: {
              columnWidth: "50%",
            },
          },
        });
        this.syncRenderedSeriesHiddenState(
          weatherSeries,
          this.weatherSeriesHiddenState,
        );
        if (isModeChange) {
          setTimeout(() => {
            window.dispatchEvent(new Event("resize"));
          }, 0);
        }
        return;
      }

      // Extract and prepare the dateList, etas, cetas, for updating chart later
      const etas =
        claimedFieldETsSubset
          .map((claimedFieldET: { eta: number }) =>
            this.toDisplayUnit(claimedFieldET.eta),
          )
          ?.slice(-this.timeRangeFilter[this.timeRange]) ?? [];
      const cetas =
        claimedFieldETsSubset
          .map((claimedFieldET: { ceta: number }) =>
            this.toDisplayUnit(claimedFieldET.ceta),
          )
          ?.slice(-this.timeRangeFilter[this.timeRange]) ?? [];
      // Extract the target water, if target unit is feet, * 0.05 else use the original value
      const targetInches =
        this.selectedclaimedField?.total_target_water_unit === "Feet" ?
          this.selectedclaimedField.total_target_water * 12
        : this.selectedclaimedField?.total_target_water;
      const target = this.toDisplayUnit(targetInches);
      const targetLineData = new Array(cetas.length).fill(target);
      // Generate annotations
      const annotations = this.configCropAnnotations();
      // Rebuild ET series while restoring prior legend visibility from ET map.
      const etSeries = this.applyPersistedSeriesHiddenState(
        [
          {
            name: "Daily Precipitation",
            data: precipitation,
            color: "#06b6d4",
            type: "column",
          },
          {
            name: "Daily ET",
            data: etas,
            color: "#1A56DB",
            type: "area",
          },
          {
            name: "Cumulative ET",
            data: cetas, // Example transformation for the second series
            color: "#7E3BF2",
            type: "area",
            hidden: true,
          },
          {
            name: "Target Water",
            data: targetLineData, // Example transformation for the second series
            color: "#C27803",
            type: "area",
            hidden: true,
          },
        ].filter(
          (series) =>
            series.name !== "Target Water" ||
            this.selectedclaimedField?.total_target_type !== "Applied Water",
        ),
        this.etSeriesHiddenState,
        this.defaultHiddenEtSeries,
      );
      // Update Main Chart
      this.chart?.updateOptions({
        grid: {
          padding: {
            // bottom: 5, // Increase this to create more space at the bottom
            left: 50,
            right: 40,
          },
        },
        series: etSeries,
        tooltip: {
          enabled: true,
          shared: true,
          intersect: false,
          followCursor: true,
          fixed: { enabled: false },
          hideEmptySeries: true,
          x: {
            show: false,
          },
        },
        stroke: {
          width: [2, 5, 5, 3],
          curve: "smooth",
          dashArray: [0, 0, 0, 5],
        },
        xaxis: {
          crosshairs: { show: false },
          categories: dateList,
          tickAmount: 12,
          formatter: function (value: number) {
            return value;
          },
        },
        yaxis: [
          {
            seriesName: "Daily Precipitation",
            opposite: false,
            title: {
              text: `Daily Precip (${this.displayUnitLabel()})`,
              style: {
                cssClass:
                  "text-sm font-normal fill-gray-500 dark:fill-gray-400",
              },
            },
            min: 0,
            show: true,
            labels: {
              show: true,
              style: {
                fontFamily: "Inter, sans-serif",
                cssClass:
                  "text-xs font-normal fill-gray-500 dark:fill-gray-400",
              },
              formatter: function (value: number) {
                // ApexCharts may pass undefined or non-numeric values on some calls
                if (typeof value === "number" && value > 0.02) {
                  // Format numeric values to 1 decimal place
                  return value.toFixed(2);
                }
                // Otherwise, return something safe, like an empty string or the raw value
                return 0;
              },
            },
          },
          {
            seriesName: "Daily ET",
            opposite: true,
            min: 0,
            title: {
              text: `Evapotranspiration (${this.displayUnitLabel()})`,
              style: {
                cssClass:
                  "text-sm font-medium fill-gray-500 dark:fill-gray-400",
              },
            },
            show: true,
            labels: {
              show: true,
              style: {
                fontFamily: "Inter, sans-serif",
                cssClass:
                  "text-xs font-normal fill-gray-500 dark:fill-gray-400",
              },
              formatter: function (value: number) {
                // ApexCharts may pass undefined or non-numeric values on some calls
                if (typeof value === "number") {
                  // Format numeric values to 1 decimal place
                  return value.toFixed(2);
                }
                // Otherwise, return something safe, like an empty string or the raw value
                return "";
              },
            },
          },
        ],
        legend: {
          show: true,
          position: "top",
          labels: {
            colors: "#64748b",
          },
          clusterGroupedSeries: false,
        },
        annotations: {
          xaxis: annotations,
        },
        plotOptions: {
          bar: {
            columnWidth: "50%",
          },
        },
      });
    }
    // Check the case of PerennialTree and open the isPerennialTreeMode — render on the main chart
    if (this.selectedclaimedField) {
      if (
        this.selectedclaimedField.isperennialtree &&
        this.isPerennialTreeMode
      ) {
        this.captureSeriesHiddenState(
          this.chart,
          this.perennialSeriesHiddenState,
        );
        // Display the forecasting chart
        const crop1 = this.cropDict[this.selectedclaimedField.crop_type_01]; // isPerennailTree means there is only one crop in this water year.
        // Extract the target water, if target unit is feet, * 0.05 else use the original value
        const targetInches =
          this.selectedclaimedField.total_target_water_unit === "Feet" ?
            this.selectedclaimedField.total_target_water * 12
          : this.selectedclaimedField.total_target_water;
        const target = this.toDisplayUnit(targetInches) ?? 0;
        // Extract & Tranform cetas and dateList, Whole water year's data (including forcasting data)
        const cetas = this.claimedFieldETs
          .map((claimedFieldET: { ceta: number }) =>
            this.toDisplayUnit(claimedFieldET.ceta),
          )
          ?.slice(-this.timeRangeFilter["All Water Year"]);
        const dateList = this.claimedFieldETs
          .map((claimedFieldET: { et_date: string }) => claimedFieldET.et_date)
          ?.slice(-this.timeRangeFilter["All Water Year"]);

        // Get the index of the current date
        const index = dateList.indexOf(this.currentDate.format("YYYY-MM-DD"));
        // Extract the solid part and dashed part
        const solidData = cetas
          .slice(0, index + 1)
          .concat(new Array(cetas.length - index - 1).fill(null));
        const dashedData = new Array(Math.max(0, index))
          .fill(null)
          .concat(cetas.slice(Math.max(0, index)));
        const perennialSeries = this.applyPersistedSeriesHiddenState(
          [
            {
              name: "current cumulative ET",
              data: solidData,
              color: "#7E3BF2",
            },
            {
              name: "forecasted cumulative ET",
              data: dashedData,
              color: "#AC94FA",
            },
            {
              name: "historical cumulative ET - 85th percentile",
              data: Object.values(
                this.convertToInches(perennialTreeQ85[crop1]),
              ).map((v) => (typeof v === "number" ? this.toDisplayUnit(v) : v)),
              color: "#9F580A",
            },
            {
              name: "historical cumulative average ET",
              data: Object.values(
                this.convertToInches(perennialTreeMean[crop1]),
              ).map((v) => (typeof v === "number" ? this.toDisplayUnit(v) : v)),
              color: "#F05252",
            },
            {
              name: "historical cumulative ET - 15th percentile",
              data: Object.values(
                this.convertToInches(perennialTreeQ15[crop1]),
              ).map((v) => (typeof v === "number" ? this.toDisplayUnit(v) : v)),
              color: "#31C48D",
            },
          ],
          this.perennialSeriesHiddenState,
        );
        // Reapply visibility by name after ApexCharts swaps the series.
        this.chart
          ?.updateOptions({
            series: perennialSeries,
            tooltip: {
              enabled: true,
              shared: true,
              intersect: false,
              followCursor: true,
              fixed: { enabled: false },
              hideEmptySeries: true,
              y: {
                formatter: (value: number) =>
                  typeof value === "number" && Number.isFinite(value) ?
                    value.toFixed(2)
                  : "N/A",
              },
              x: {
                show: false,
              },
            },
            fill: {
              opacity: [0, 0, 0, 0, 0, 1],
            },
            stroke: {
              width: [6, 6, 3, 3, 3, 3],
              dashArray: [0, 5, 0, 0, 0, 0],
            },
            annotations: {
              xaxis: [
                {
                  x: `${this.currentDate.format("YYYY-MM-DD")}`,
                  strokeDashArray: 5,
                  borderWidth: 3,
                  borderColor: "#7E3BF2",
                  label: {
                    borderColor: "#7E3BF2",
                    style: {
                      color: "#D1D5DB",
                      background: "#7E3BF2",
                    },
                    orientation: "horizontal",
                    text: `Latest Date: ${this.currentDate.format("YYYY-MM-DD")}`,
                  },
                },
              ],
              yaxis:
                (
                  this.selectedclaimedField.total_target_type ===
                  "Applied Water"
                ) ?
                  []
                : [
                    {
                      y: Number(target),
                      strokeDashArray: 5,
                      borderWidth: 3,
                      borderColor: "#1A56DB",
                      label: {
                        borderColor: "#1A56DB",
                        style: {
                          color: "#D1D5DB",
                          background: "#1A56DB",
                        },
                        text: "Target Water: " + target.toFixed(2),
                      },
                    },
                  ],
            },
            xaxis: {
              crosshairs: { show: false },
              categories: dateList,
              tickAmount: 12,
              formatter: function (value: number) {
                return value;
              },
            },
            yaxis: {
              min: 0,
              max: Math.max(
                this.toDisplayUnit(
                  (
                    Object.values(
                      this.convertToInches(perennialTreeQ85[crop1]),
                    ) as number[]
                  )?.at(-1) ?? 0,
                ) ?? 0 + 2,
                (
                  this.selectedclaimedField.total_target_type ===
                    "Applied Water"
                ) ?
                  0
                : target + 2,
              ),
              tickAmount: 10,
              title: {
                text: `Cumulative ET (${this.displayUnitLabel()})`,
                style: {
                  fontFamily: "Inter, sans-serif",
                  cssClass:
                    "text-xs font-normal fill-gray-500 dark:fill-gray-400",
                },
              },
              labels: {
                style: {
                  fontFamily: "Inter, sans-serif",
                  cssClass:
                    "text-xs font-normal fill-gray-500 dark:fill-gray-400",
                },
                formatter: function (value: number) {
                  if (typeof value === "number") {
                    return value.toFixed(2);
                  }
                  return "No Data";
                },
              },
            },
            // Re-update due to switching mode will cause the data to persist between switching
            grid: {
              padding: {
                left: 50,
              },
            },
            legend: {
              show: true,
              position: "top",
              labels: {
                colors: "#64748b",
              },
              clusterGroupedSeries: false,
            },
            plotOptions: {
              bar: {
                columnWidth: "50%",
              },
            },
          })
          .then(() => {
            this.syncRenderedSeriesHiddenState(
              perennialSeries,
              this.perennialSeriesHiddenState,
            );
          });
      }
    } else {
      console.error("No selected claimed field found for updating chart.");
    }
    // DispatchEvent resize, to refresh the chart area.
    if (isModeChange) {
      setTimeout(() => {
        window.dispatchEvent(new Event("resize"));
      }, 0);
    }
  }

  // Function for transforming millimeters to inches
  convertToInches(perennial: any) {
    return Object.fromEntries(
      Object.entries(perennial).map(([key, value]) => [
        key,
        typeof value === "number" ? value * 0.0393700787 : value,
      ]),
    );
  }

  // Function to convert JSON to CSV
  convertToCsvManagerOrEntity(dataArray: any[], acres: number): string {
    // Join headers with commas to form the first row of the CSV
    const headerRow =
      this.buildEtCsvHeaderAliasesForUserType("managerOrEntity").join(",");
    // Map each object in the array to a row of CSV values
    const rows = dataArray.map((item) =>
      this.CSVHeadersManagerOrEntity.map((field) => {
        if (item["et_date"] > this.currentDate.format("YYYY-MM-DD")) {
          return null; // Exclude this item
        }
        if (field === "acres") return formatNumber(acres, 1);
        const value = item[field];
        // Format numeric fields as decimals
        return field !== "et_date" ? formatNumber(value) : value; // Keep date or other non-numeric fields unchanged
      })
        .map(escapeCSV)
        .join(","),
    );
    // Join all rows with newline characters to form the CSV string
    return [headerRow, ...rows].join("\n");
  }

  // Function to download ET Chart as CSV
  downloadAppliedWaterEstimate(): void {
    const result = this.appliedWaterPreviewResult;
    if (!result) return;

    const headers = [
      "Month",
      "Monthly Surface Water Applied",
      "Monthly Groundwater Applied - Medium",
      "Monthly Groundwater Applied - High",
      "Monthly Groundwater Applied - Low",
    ];
    const formatAppliedWaterValue = (
      value: number | null | undefined,
    ): string =>
      Number.isFinite(Number(value)) ? Number(value).toFixed(2) : "0.00";
    const rows = result.months.map((month, index) =>
      [
        month,
        formatAppliedWaterValue(result.surfaceWaterAppliedMed[index]),
        formatAppliedWaterValue(result.groundWaterAppliedMed[index]),
        formatAppliedWaterValue(result.groundWaterAppliedHigh[index]),
        formatAppliedWaterValue(result.groundWaterAppliedLow[index]),
      ]
        .map(escapeCSV)
        .join(","),
    );
    const csvData = [headers.map(escapeCSV).join(","), ...rows].join("\n");
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${this.title ?? "field"}_applied_water.csv`;
    anchor.click();
    window.URL.revokeObjectURL(url);
    anchor.remove();
  }

  async downloadETManagerOrEntity(): Promise<void> {
    if (this.selectedclaimedField) {
      await this.ensureEtValuesForField(this.selectedclaimedField);
      const csvData = this.convertToCsvManagerOrEntity(
        this.convertEtRowsToDisplayUnit(
          this.selectedclaimedField.userFieldEtvalues ?? [],
        ),
        this.selectedclaimedField.acres,
      );
      const blob = new Blob([csvData], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      // Create a temporary anchor element and trigger the download
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = this.title + "_daily.csv";
      anchor.click();
      // Clean up
      window.URL.revokeObjectURL(url);
      anchor.remove();
    }
  }

  // Function to convert JSON to CSV
  convertToCsv(
    dataArray: any[],
    acres: number,
    field_name: string,
    entity: string,
    cropParam: any,
  ): string {
    // Join headers with commas to form the first row of the CSV
    const headerRow =
      this.buildEtCsvHeaderAliasesForUserType("grower").join(",");
    // Map each object in the array to a row of CSV values
    const rows = dataArray.map((item) =>
      this.CSVHeaders.map((field) => {
        if (item["et_date"] > this.currentDate.format("YYYY-MM-DD")) {
          return null; // Exclude this item
        }
        // Assign the Crop to the CSV
        if (field === "crop_type") {
          if (cropParam.isperennialtree) {
            // Set up the crop_type_01 as the crop_type if it is perennial tree
            return cropParam.crop_type_01;
          } else {
            // Else, compare the dates
            if (
              moment
                .utc(item["et_date"])
                .isBetween(
                  moment.utc(cropParam.ls_start_01),
                  moment.utc(cropParam.ls_end_01),
                  "day",
                  "[]",
                )
            ) {
              return cropParam.crop_type_01;
            } else if (
              cropParam.ls_start_02 &&
              moment
                .utc(item["et_date"])
                .isBetween(
                  moment.utc(cropParam.ls_start_02),
                  moment.utc(cropParam.ls_end_02),
                  "day",
                  "[]",
                )
            ) {
              return cropParam.crop_type_02;
            } else if (
              cropParam.ls_start_03 &&
              moment
                .utc(item["et_date"])
                .isBetween(
                  moment.utc(cropParam.ls_start_03),
                  moment.utc(cropParam.ls_end_03),
                  "day",
                  "[]",
                )
            ) {
              return cropParam.crop_type_03;
            } else {
              return "Fallow";
            }
          }
        }
        if (field === "field_name") return field_name;
        if (field === "entity") return entity === "None" ? "" : entity;
        if (field === "acres") return formatNumber(acres, 1);
        const value = item[field];
        // Format numeric fields as decimals
        return field !== "et_date" ? formatNumber(value) : value; // Keep date or other non-numeric fields unchanged
      })
        .map(escapeCSV)
        .join(","),
    );
    // Join all rows with newline characters to form the CSV string
    return [headerRow, ...rows].join("\n");
  }

  readonly downloadAllETs = async () => {
    this.isDownloadAllDisabled.set(true);
    this.loadingService.start({
      token: "download-all-ets",
      message: "Downloading data",
    });
    try {
      const claimedFieldsWithEt =
        await this.getAllClaimedFieldsWithEtForDownload();
      this.cacheDownloadedEtValues(claimedFieldsWithEt);
      const data = await convertAllETToCSV(
        claimedFieldsWithEt,
        this.buildEtCsvHeaderAliasesForUserType("grower"),
        this.CSVHeaders,
        this.currentDate,
        this.displayUnit(),
      );
      const blob = new Blob([data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      // Create a temporary anchor element and trigger the download
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "all_fields_daily.csv";
      anchor.click();
      // Clean up
      window.URL.revokeObjectURL(url);
      anchor.remove();
    } finally {
      this.loadingService.stop("download-all-ets");
      this.isDownloadAllDisabled.set(false);
    }
  };

  // Function to download ET Chart as CSV
  async downloadET(): Promise<void> {
    if (this.selectedclaimedField) {
      await this.ensureEtValuesForField(this.selectedclaimedField);
      // Define the object of cropParam
      const cropParam = {
        isperennialtree: this.selectedclaimedField.isperennialtree,
        crop_type_01: this.selectedclaimedField.crop_type_01,
        ls_start_01: moment
          .utc(this.selectedclaimedField.ls_start_01)
          .format("YYYY-MM-DD"),
        ls_end_01: moment
          .utc(this.selectedclaimedField.ls_end_01)
          .format("YYYY-MM-DD"),
        crop_type_02:
          this.selectedclaimedField.crop_type_02 ?
            this.selectedclaimedField.crop_type_02
          : "",
        ls_start_02:
          this.selectedclaimedField.ls_start_02 ?
            moment
              .utc(this.selectedclaimedField.ls_start_02)
              .format("YYYY-MM-DD")
          : "",
        ls_end_02:
          this.selectedclaimedField.ls_end_02 ?
            moment.utc(this.selectedclaimedField.ls_end_02).format("YYYY-MM-DD")
          : "",
        crop_type_03:
          this.selectedclaimedField.crop_type_03 ?
            this.selectedclaimedField.crop_type_03
          : "",
        ls_start_03:
          this.selectedclaimedField.ls_start_03 ?
            moment
              .utc(this.selectedclaimedField.ls_start_03)
              .format("YYYY-MM-DD")
          : "",
        ls_end_03:
          this.selectedclaimedField.ls_end_03 ?
            moment.utc(this.selectedclaimedField.ls_end_03).format("YYYY-MM-DD")
          : "",
      };
      // Convert to CSV data
      const entitiesLabel = (this.selectedclaimedField.entities ?? [])
        .map((e: { name: string; email: string }) => e.name)
        .join("; ");
      const csvData = this.convertToCsv(
        this.convertEtRowsToDisplayUnit(
          this.selectedclaimedField.userFieldEtvalues ?? [],
        ),
        this.selectedclaimedField.acres,
        this.selectedclaimedField.field_name,
        entitiesLabel,
        cropParam,
      );
      const blob = new Blob([csvData], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      // Create a temporary anchor element and trigger the download
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = this.title + "_daily.csv";
      anchor.click();
      // Clean up
      window.URL.revokeObjectURL(url);
      anchor.remove();
    }
  }

  private buildWeatherCsvHeaders(includeEntity = false): string[] {
    const precipUnitLabel = this.displayUnitLabel(true);
    return [
      "Date",
      "Field Name",
      ...(includeEntity ? ["Entity Name"] : []),
      "Acres",
      "Min Temperature (F)",
      "Max Temperature (F)",
      `Precipitation (${precipUnitLabel})`,
      `Cumulative Precipitation (${precipUnitLabel})`,
      "Growing Degree Days (Day)",
      "Chilling Portions (Chilling Portion)",
    ];
  }

  private buildWeatherCsvRows(
    field: any,
    fieldWeather: any,
    includeEntity = false,
  ): string[] {
    if (!Array.isArray(field.userFieldEtvalues)) {
      return [];
    }

    const claimedFieldETsSubset = field.userFieldEtvalues.filter(
      (claimedFieldET: any) =>
        claimedFieldET.et_date >=
          startDateCalendarWaterYear().format("YYYY-MM-DD") &&
        claimedFieldET.et_date <= this.currentDate.format("YYYY-MM-DD"),
    );

    const dateList = claimedFieldETsSubset.map(
      (claimedFieldET: { et_date: string }) => claimedFieldET.et_date,
    );
    const dateListToToday = [
      ...dateList,
      ...this.buildDatesThroughWeatherCurrentDate(
        dateList[dateList.length - 1],
      ),
    ];
    const forecastMinDaily =
      fieldWeather?.forecast?.daily?.temperature_2m_min ?? [];
    const forecastMaxDaily =
      fieldWeather?.forecast?.daily?.temperature_2m_max ?? [];
    const historicalAvgMin = fieldWeather?.historicalAvg?.min ?? [];
    const historicalAvgMax = fieldWeather?.historicalAvg?.max ?? [];
    const forecastHorizon = Math.max(
      forecastMinDaily.length,
      forecastMaxDaily.length,
      historicalAvgMin.length,
      historicalAvgMax.length,
    );
    const futureDateCount = Math.max(0, forecastHorizon - 1);
    const bridgeDaysCount = Math.max(
      0,
      dateListToToday.length - dateList.length,
    );
    const futureDates = this.buildFutureDates(
      dateListToToday[dateListToToday.length - 1],
      futureDateCount,
    );
    const weatherDateList = [...dateListToToday, ...futureDates];
    const todayDateStr = (this.todayDate ?? this.currentDate).format(
      "YYYY-MM-DD",
    );
    const currentDateIndex = Math.max(0, weatherDateList.indexOf(todayDateStr));
    const alignSeriesToCurrentDate = (seriesData: any[] = []) =>
      new Array(currentDateIndex)
        .fill(null)
        .concat(seriesData)
        .slice(-weatherDateList.length);
    const precipitationDownload = claimedFieldETsSubset.map(
      (claimedFieldET: { prcp: number }) => claimedFieldET.prcp,
    );
    const precipitationExtendedDownload = [
      ...precipitationDownload,
      ...new Array(bridgeDaysCount).fill(0),
      ...new Array(futureDateCount).fill(null),
    ];
    const cprecipitation = claimedFieldETsSubset.map(
      (claimedFieldET: { cprcp: number }) => claimedFieldET.cprcp,
    );
    const lastCumulativePrecip =
      cprecipitation[cprecipitation.length - 1] ?? null;
    const cprecipitationExtended = [
      ...cprecipitation,
      ...new Array(bridgeDaysCount).fill(lastCumulativePrecip),
      ...new Array(futureDateCount).fill(null),
    ];
    const gddObserved = fieldWeather?.historical?.daily?.gdd_list ?? [];
    const gdd = [...gddObserved, ...new Array(futureDateCount).fill(null)];
    const chill = [
      ...(fieldWeather?.chilling_hours ?? []),
      ...new Array(futureDateCount).fill(null),
    ];
    const minObserved =
      fieldWeather?.historical?.daily?.temperature_2m_min ?? [];
    const maxObserved =
      fieldWeather?.historical?.daily?.temperature_2m_max ?? [];
    const minTem = [...minObserved, ...new Array(futureDateCount).fill(null)];
    const maxTem = [...maxObserved, ...new Array(futureDateCount).fill(null)];
    const forecastMinTem = alignSeriesToCurrentDate(forecastMinDaily);
    const forecastMaxTem = alignSeriesToCurrentDate(forecastMaxDaily);
    const resolveTemp = (observed: any, forecasted: any): any =>
      typeof observed === "number" ? observed : forecasted;
    const toCsvNumber = (value: any): string =>
      typeof value === "number" && Number.isFinite(value) ?
        String(formatNumber(value))
      : "";
    const toCsvInteger = (value: any): string =>
      typeof value === "number" && Number.isFinite(value) ?
        String(Math.round(value))
      : "";
    const toFieldDisplayUnit = (inches: number | null | undefined) => {
      if (inches == null) return null;
      if (this.isFeetUnit()) return inches / 12;
      return convertInchesToDisplayUnit(
        inches,
        field.acres,
        this.isAcreFeetUnit(),
      );
    };

    return weatherDateList.map((date: string, index: number) => {
      const row = [
        moment(date).format("M/D/YYYY"),
        field.field_name ?? "",
        ...(includeEntity ?
          [field.entity === "None" ? "" : (field.entity ?? "")]
        : []),
        formatNumber(field.acres ?? 0, 1),
        toCsvNumber(resolveTemp(minTem[index], forecastMinTem[index])),
        toCsvNumber(resolveTemp(maxTem[index], forecastMaxTem[index])),
        toCsvNumber(toFieldDisplayUnit(precipitationExtendedDownload[index])),
        toCsvNumber(toFieldDisplayUnit(cprecipitationExtended[index])),
        toCsvInteger(gdd[index]),
        toCsvNumber(chill[index]),
      ];
      return row.map(escapeCSV).join(",");
    });
  }

  async downloadWeatherData() {
    // Edge Case Checking
    if (!this.selectedclaimedField) {
      return;
    }
    await this.ensureEtValuesForField(this.selectedclaimedField);
    if (!Array.isArray(this.claimedFieldETs)) {
      return;
    }
    const headers = this.buildWeatherCsvHeaders();
    const rows = this.buildWeatherCsvRows(
      this.selectedclaimedField,
      this.fieldWeather,
    );
    // Define the download operation
    const csvData = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csvData], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${this.title ?? "weather"}_weather_daily.csv`;
    anchor.click();
    window.URL.revokeObjectURL(url);
    anchor.remove();
  }

  readonly downloadAllWeatherData = async () => {
    if (!this.claimedFields?.length || this.isDownloadAllDisabled()) {
      return;
    }

    this.isDownloadAllDisabled.set(true);
    this.loadingService.start({
      token: "download-all-weather-data",
      message: "Downloading weather data",
    });

    try {
      const rows: string[] = [];
      for (const field of this.claimedFields) {
        try {
          await this.ensureEtValuesForField(field);
          const fieldWeather = await this.getWeatherDataForField(field);
          rows.push(...this.buildWeatherCsvRows(field, fieldWeather, true));
        } catch (error) {
          console.error(
            `Cannot Get Weather Data for ${field.field_name}`,
            error,
          );
        }
      }

      const csvData = [
        this.buildWeatherCsvHeaders(true).join(","),
        ...rows,
      ].join("\n");
      const blob = new Blob([csvData], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "all_fields_weather_daily.csv";
      anchor.click();
      window.URL.revokeObjectURL(url);
      anchor.remove();
    } finally {
      this.loadingService.stop("download-all-weather-data");
      this.isDownloadAllDisabled.set(false);
    }
  };
}
